import { BLOCKS, BY_FN, CAT_COLOR, KEYWORDS } from './catalog.js';
import { allLists, allVariables } from './sb3.js';

const KIND_LABEL = {
  hat: 'hat',
  stack: 'block',
  c: 'block',
  cs: 'block',
  cap: 'block',
  reporter: 'reporter',
  boolean: 'boolean',
  kw: 'keyword',
  var: 'variable',
  list: 'list',
  proc: 'my block',
  arg: 'argument',
  menu: 'value',
};

const STMT_KINDS = new Set(['stack', 'c', 'cs', 'cap', 'proc']);
const EXPR_KINDS = new Set(['reporter', 'boolean', 'var', 'list', 'arg']);
const BOOLEAN_CALLS = new Set(['if', 'repeat_until', 'wait_until', 'not']);

const MENU_KEYS = {
  space: true, any: true, 'left arrow': true, 'right arrow': true, 'up arrow': true, 'down arrow': true,
  enter: true, shift: true, control: true, escape: true,
};

export class Completer {
  constructor(app) {
    this.app = app;
    this.ta = document.getElementById('code-input');
    this.items = [];
    this.active = 0;
    this.token = { start: 0, end: 0, word: '' };
    this.open = false;
    this.forced = false;
    this._dismissed = null;
    this.menu = document.createElement('div');
    this.menu.id = 'ac-menu';
    this.menu.className = 'ac-menu hidden';
    this.menu.setAttribute('role', 'listbox');
    document.body.appendChild(this.menu);
    this.ta.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.code === 'Space') {
        e.preventDefault();
        this.forced = true;
        this.update(true);
      }
    });
    this.ta.addEventListener('blur', () => {
      setTimeout(() => {
        if (!this.menu.contains(document.activeElement)) this.hide();
      }, 120);
    });
    this.menu.addEventListener('mousedown', (e) => {
      const row = e.target.closest('[data-ac]');
      if (!row) return;
      e.preventDefault();
      this.active = Number(row.dataset.ac);
      this.accept();
    });
    document.getElementById('code-scroll')?.addEventListener('scroll', () => {
      if (this.open) this.place();
    });
    window.addEventListener('resize', () => {
      if (this.open) this.place();
    });
  }

  onKey(e) {
    if (e.key === ')' && this.skipClosingParen()) {
      e.preventDefault();
      return true;
    }
    if (!this.open) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.active = (this.active + 1) % this.items.length;
      this.paint();
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.active = (this.active - 1 + this.items.length) % this.items.length;
      this.paint();
      return true;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      this.accept();
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this._dismissed = this.token.word;
      this.hide();
      return true;
    }
    return false;
  }

  onInput() {
    if (this._skip) return;
    this.forced = false;
    this.update(false);
  }

  hide() {
    this.open = false;
    this.forced = false;
    this.menu.classList.add('hidden');
    this.menu.innerHTML = '';
  }

  update(force) {
    const ta = this.ta;
    if (this.app.tab !== 'code') return this.hide();
    const pos = ta.selectionStart;
    if (pos !== ta.selectionEnd) return this.hide();
    const ctx = analyzeContext(ta.value, pos);
    this.token = ctx.token;
    const word = ctx.token.word;
    const forced = force || this.forced;
    if (!forced) {
      if (word.length < 1) return this.hide();
      if (/^\d+$/.test(word)) return this.hide();
      if (this._dismissed != null && word === this._dismissed) return this.hide();
    }
    if (this._dismissed != null && word !== this._dismissed) this._dismissed = null;
    const items = suggest(this.app, ta.value, pos, { force: forced }).filter((item) => {
      if (forced) return true;
      const insertId = (item.filter || '').split(/\s+/)[0].toLowerCase();
      const w = word.toLowerCase();
      if (w.length <= 2) {
        return insertId.startsWith(w) || item.label.toLowerCase().startsWith(w);
      }
      return true;
    });
    if (!items.length) return this.hide();
    this.items = items;
    this.active = 0;
    this.open = true;
    this.paint();
    this.place();
  }

  paint() {
    this.menu.classList.remove('hidden');
    this.menu.innerHTML = '';
    this.items.forEach((item, i) => {
      const row = document.createElement('div');
      row.className = 'ac-item' + (i === this.active ? ' active' : '');
      row.dataset.ac = String(i);
      row.setAttribute('role', 'option');
      const left = document.createElement('span');
      left.className = 'ac-left';
      const dot = document.createElement('span');
      dot.className = 'ac-dot';
      dot.style.background = item.color || '#888';
      const lab = document.createElement('span');
      lab.className = 'ac-label';
      lab.textContent = item.label;
      left.append(dot, lab);
      const kind = document.createElement('span');
      kind.className = 'ac-kind';
      kind.textContent = item.kindLabel;
      row.append(left, kind);
      this.menu.appendChild(row);
    });
    this.menu.querySelector('.ac-item.active')?.scrollIntoView({ block: 'nearest' });
  }

  place() {
    const c = caretCoords(this.ta);
    const menu = this.menu;
    menu.style.left = `${Math.min(c.left, window.innerWidth - 280)}px`;
    const below = c.bottom + 4;
    menu.style.top = `${below}px`;
    menu.style.visibility = 'hidden';
    menu.classList.remove('hidden');
    const h = menu.offsetHeight;
    menu.style.visibility = '';
    if (below + h > window.innerHeight - 8 && c.top - 4 - h > 8) {
      menu.style.top = `${c.top - 4 - h}px`;
    }
  }

  accept() {
    const item = this.items[this.active];
    if (!item || !this.open) return;
    const ta = this.ta;
    const { start, end } = this.token;
    const before = ta.value.slice(0, start);
    let insert = item.insert;
    if (item.kind === 'hat' && /\bwhen\s+$/.test(before)) {
      insert = insert.replace(/^when\s+/, '');
    }
    const lineStart = before.lastIndexOf('\n') + 1;
    const indent = before.slice(lineStart).match(/^ */)[0];
    let extra = '';
    if (insert.endsWith(':') && !ta.value.slice(end).startsWith('\n')) {
      extra = `\n${indent}    `;
    }
    ta.value = ta.value.slice(0, start) + insert + extra + ta.value.slice(end);
    const sel = item.select;
    if (sel) {
      ta.selectionStart = start + sel[0];
      ta.selectionEnd = start + sel[1];
    } else {
      ta.selectionStart = ta.selectionEnd = start + insert.length + extra.length;
    }
    this.hide();
    this._skip = true;
    this.app.onCode();
    this._skip = false;
    ta.focus();
  }

  skipClosingParen() {
    const ta = this.ta;
    const v = ta.value;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const lineStart = v.lastIndexOf('\n', start - 1) + 1;
    const nl = v.indexOf('\n', start);
    const line = v.slice(lineStart, nl < 0 ? v.length : nl);
    const col = start - lineStart;
    if (commentOnly(line, col) || quotedToken(line, col, lineStart)) return false;
    const next = skipClosingIndex(v, end);
    if (next == null) return false;
    ta.selectionStart = ta.selectionEnd = next;
    return true;
  }
}

export function skipClosingIndex(value, end) {
  let i = end;
  while (i < value.length && value[i] === ' ') i++;
  if (value[i] !== ')') return null;
  return i + 1;
}

export function suggest(app, script, pos, opts = {}) {
  const ctx = analyzeContext(script, pos);
  if (ctx.slot === 'none') return [];
  const items = collect(app, script, ctx).filter((item) => allowedIn(item, ctx));
  return rank(items, ctx.token.word, ctx).slice(0, 8);
}

export function analyzeContext(script, pos) {
  const text = String(script || '');
  const p = Math.max(0, Math.min(pos ?? text.length, text.length));
  const lineStart = text.lastIndexOf('\n', p - 1) + 1;
  const nl = text.indexOf('\n', p);
  const lineEnd = nl < 0 ? text.length : nl;
  const line = text.slice(lineStart, lineEnd);
  const col = p - lineStart;
  const indent = (line.match(/^ */) || [''])[0].length;
  const ident = identToken(text, p);
  const toCursor = line.slice(0, col);
  const identStartCol = ident.start - lineStart;
  const toWord = identStartCol >= 0 && identStartCol <= col ? line.slice(0, identStartCol) : toCursor;

  if (commentOnly(line, col)) {
    return { slot: 'none', token: ident, indent, reason: 'comment' };
  }
  const quoted = quotedToken(line, col, lineStart);
  if (quoted) {
    return {
      slot: 'string',
      token: quoted,
      indent,
      fn: innermostFn(toCursor),
      want: 'string',
    };
  }

  const depth = parenDepth(toCursor);
  const lead = toWord.trim();
  const typed = toCursor.trim();

  if (indent === 0 && (/^define\b/.test(typed) || /^define\b/.test(lead))) {
    return { slot: 'none', token: ident, indent, reason: 'define' };
  }

  if (depth > 0) {
    const fn = innermostFn(toCursor);
    return {
      slot: 'expr',
      token: ident,
      indent,
      fn,
      want: BOOLEAN_CALLS.has(fn) ? 'boolean' : 'expr',
    };
  }

  if (indent === 0 && (/^when\b/.test(lead) || /^when\b/.test(typed))) {
    if (!lead && /^when$/.test(ident.word)) {
      return { slot: 'header', token: ident, indent };
    }
    return { slot: 'hat', token: ident, indent };
  }

  if (/^(if|wait_until|repeat_until)\b/.test(lead)) {
    return { slot: 'expr', token: ident, indent, want: 'boolean', fn: lead.split(/\s+/)[0] };
  }
  if (!lead && /^(if|wait_until|repeat_until)$/.test(ident.word)) {
    return { slot: indent === 0 ? 'header-typed' : 'stmt', token: ident, indent, elseOk: elseAllowed(text, p, indent) };
  }

  if (/^[A-Za-z_][A-Za-z0-9_]*\s*(?:\+|-)?=\s*/.test(typed) && !toCursor.includes('(')) {
    return { slot: 'expr', token: ident, indent, want: 'expr' };
  }

  if (lead && !/^(if|forever|repeat|repeat_until|wait_until)\b/.test(lead)) {
    return { slot: 'none', token: ident, indent, reason: 'one-stmt' };
  }
  if (lead && /^(forever|repeat)\b/.test(lead)) {
    return { slot: 'none', token: ident, indent, reason: 'one-stmt' };
  }

  if (indent === 0 && !lead) {
    return {
      slot: ident.word ? 'header-typed' : 'header',
      token: ident,
      indent,
      elseOk: false,
    };
  }

  return {
    slot: 'stmt',
    token: ident,
    indent,
    elseOk: elseAllowed(text, p, indent),
  };
}

function allowedIn(item, ctx) {
  const slot = ctx.slot;
  if (slot === 'none') return false;
  if (slot === 'string') return item.kind === 'menu';
  if (slot === 'hat') return item.kind === 'hat' && !/^when\s/.test(item.insert);
  if (slot === 'header') {
    return item.kind === 'hat' || (item.kind === 'kw' && (item.filter === 'when' || item.filter === 'define'));
  }
  if (slot === 'header-typed') {
    return item.kind === 'hat'
      || (item.kind === 'kw' && (item.filter === 'when' || item.filter === 'define'))
      || STMT_KINDS.has(item.kind)
      || (item.kind === 'kw' && item.stmtKw);
  }
  if (slot === 'stmt') {
    if (item.kind === 'hat' || (item.kind === 'kw' && (item.filter === 'when' || item.filter === 'define'))) return false;
    if (item.kind === 'kw' && item.filter === 'else') return !!ctx.elseOk;
    return STMT_KINDS.has(item.kind) || item.kind === 'var' || (item.kind === 'kw' && item.stmtKw);
  }
  if (slot === 'expr') {
    if (item.kind === 'kw') return !!item.exprKw;
    return EXPR_KINDS.has(item.kind);
  }
  return false;
}

function collect(app, script, ctx) {
  const items = [];
  const seen = new Set();
  const add = (item) => {
    const key = item.kind + ':' + item.insert;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  for (const b of BLOCKS) {
    if (b.fn === 'if_else') continue;
    const snippet = b.snippet || `${b.fn}()`;
    const kind = b.kind === 'hat' ? 'hat' : (b.kind || 'stack');
    add({
      label: snippet,
      insert: snippet,
      filter: `${b.fn} ${snippet}`,
      kind,
      kindLabel: KIND_LABEL[b.kind] || 'block',
      color: CAT_COLOR[b.cat] || '#888',
      select: firstArgRange(snippet),
      cat: b.cat,
    });
    if (kind === 'hat' && snippet.startsWith('when ')) {
      const rest = snippet.slice(5);
      add({
        label: rest,
        insert: rest,
        filter: `${b.fn} ${rest}`,
        kind: 'hat',
        kindLabel: 'hat',
        color: CAT_COLOR[b.cat] || '#888',
        select: firstArgRange(rest),
        cat: b.cat,
      });
    }
  }

  add({
    label: 'loudness > 10',
    insert: 'loudness > 10',
    filter: 'loudness greater',
    kind: 'hat',
    kindLabel: 'hat',
    color: CAT_COLOR.events,
  });
  add({
    label: 'timer > 10',
    insert: 'timer > 10',
    filter: 'timer greater',
    kind: 'hat',
    kindLabel: 'hat',
    color: CAT_COLOR.events,
  });

  const kws = [
    { kw: 'if', insert: 'if ', stmtKw: true },
    { kw: 'else', insert: 'else:', stmtKw: true },
    { kw: 'forever', insert: 'forever:', stmtKw: true },
    { kw: 'repeat', insert: 'repeat(10):', stmtKw: true, select: firstArgRange('repeat(10):') },
    { kw: 'repeat_until', insert: 'repeat_until():', stmtKw: true, select: firstArgRange('repeat_until():') },
    { kw: 'wait_until', insert: 'wait_until()', stmtKw: true, select: firstArgRange('wait_until()') },
    { kw: 'when', insert: 'when ', stmtKw: true },
    { kw: 'define', insert: 'define name():', stmtKw: true, select: [7, 11] },
    { kw: 'not', insert: 'not ', exprKw: true },
    { kw: 'and', insert: 'and ', exprKw: true },
    { kw: 'or', insert: 'or ', exprKw: true },
  ];
  for (const k of kws) {
    add({
      label: k.insert.trim(),
      insert: k.insert,
      filter: k.kw,
      kind: 'kw',
      kindLabel: 'keyword',
      color: '#d9480f',
      select: k.select || firstArgRange(k.insert),
      stmtKw: k.stmtKw,
      exprKw: k.exprKw,
    });
  }

  for (const v of allVariables(app.project, app.current)) {
    add({
      label: v.name,
      insert: v.name,
      filter: v.name,
      kind: 'var',
      kindLabel: v.local ? 'local var' : 'variable',
      color: CAT_COLOR.variables,
    });
  }
  for (const v of allLists(app.project, app.current)) {
    add({
      label: v.name,
      insert: v.name,
      filter: v.name,
      kind: 'list',
      kindLabel: 'list',
      color: CAT_COLOR.variables,
    });
  }
  for (const name of assignedNames(script)) {
    add({
      label: name,
      insert: name,
      filter: name,
      kind: 'var',
      kindLabel: 'variable',
      color: CAT_COLOR.variables,
    });
  }
  for (const name of definedProcs(script)) {
    add({
      label: `${name}()`,
      insert: `${name}()`,
      filter: name,
      kind: 'proc',
      kindLabel: 'my block',
      color: CAT_COLOR.myblocks,
      select: [name.length + 1, name.length + 1],
    });
  }
  for (const name of definedArgs(script, ctx.token.start)) {
    add({
      label: name,
      insert: name,
      filter: name,
      kind: 'arg',
      kindLabel: 'argument',
      color: CAT_COLOR.myblocks,
    });
  }

  if (ctx.slot === 'string') {
    for (const val of stringChoices(app, ctx.fn)) {
      add({
        label: val,
        insert: val,
        filter: val,
        kind: 'menu',
        kindLabel: 'value',
        color: '#c2255c',
      });
    }
  }
  return items;
}

function stringChoices(app, fn) {
  const t = app.current;
  const stage = app.project.targets.find((x) => x.isStage);
  const sprites = app.project.targets.filter((x) => !x.isStage).map((x) => x.name);
  const defs = BY_FN.get(fn) || [];
  const types = new Set();
  for (const d of defs) {
    for (const inp of d.inputs || []) types.add(inp.type);
  }
  const out = [];
  const add = (s) => { if (s && !out.includes(s)) out.push(s); };
  if (types.has('costume') || fn === 'switch_costume') {
    for (const c of t?.costumes || []) add(c.name);
  }
  if (types.has('backdrop') || fn === 'switch_backdrop' || fn === 'switch_backdrop_and_wait' || fn === 'backdrop_switches_to') {
    for (const c of stage?.costumes || []) add(c.name);
  }
  if (types.has('sound') || fn === 'start_sound' || fn === 'play_sound_until_done') {
    for (const s of t?.sounds || []) add(s.name);
  }
  if (types.has('goto') || types.has('glide') || types.has('towards') || types.has('touching') || types.has('distance') || types.has('clone')) {
    add('random position');
    add('mouse-pointer');
    add('myself');
    add('edge');
    for (const n of sprites) add(n);
  }
  if (types.has('key') || fn === 'key_pressed') {
    for (const k of Object.keys(MENU_KEYS)) add(k);
  }
  if (types.has('broadcast') || fn === 'broadcast' || fn === 'broadcast_and_wait' || fn === 'received') {
    for (const n of Object.values(stage?.broadcasts || {})) add(n);
  }
  if (types.has('penparam')) {
    add('color'); add('saturation'); add('brightness');
  }
  return out;
}

function assignedNames(script) {
  const names = new Set();
  for (const raw of String(script || '').split('\n')) {
    const t = raw.replace(/#.*$/, '').trim();
    const asg = t.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*(?:\+|-)?=/);
    if (asg && !KEYWORDS.has(asg[1])) names.add(asg[1]);
    const set = t.match(/^(?:set|change|show_variable|hide_variable)\(\s*([A-Za-z_][A-Za-z0-9_]*)/);
    if (set) names.add(set[1]);
  }
  return names;
}

function definedProcs(script) {
  const names = new Set();
  for (const raw of String(script || '').split('\n')) {
    const m = raw.replace(/#.*$/, '').trim().match(/^define\s+([A-Za-z_][A-Za-z0-9_]*)\s*\(/);
    if (m) names.add(m[1]);
  }
  return names;
}

function definedArgs(script, pos) {
  const lines = String(script || '').slice(0, pos).split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const raw = lines[i];
    const indent = raw.match(/^ */)[0].length;
    const t = raw.replace(/#.*$/, '').trim();
    if (indent === 0 && t.startsWith('define ')) {
      const m = t.match(/^define\s+[A-Za-z_][A-Za-z0-9_]*\s*\(([^)]*)\)/);
      if (!m) return [];
      return m[1].split(',').map((s) => s.trim()).filter((n) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(n));
    }
    if (indent === 0 && (t.startsWith('when ') || t.startsWith('when('))) return [];
  }
  return [];
}

function elseAllowed(script, pos, indent) {
  const lines = String(script || '').slice(0, pos).split('\n');
  lines.pop();
  for (let i = lines.length - 1; i >= 0; i--) {
    const raw = lines[i];
    if (!raw.trim() || raw.trim().startsWith('#')) continue;
    const ind = raw.match(/^ */)[0].length;
    const t = raw.replace(/#.*$/, '').trim().replace(/:$/, '');
    if (ind < indent) return false;
    if (ind === indent) return t === 'if' || t.startsWith('if ') || t.startsWith('if(');
  }
  return false;
}

function rank(items, word, ctx) {
  const w = String(word || '').toLowerCase();
  const scored = [];
  for (const item of items) {
    const s = score(item, w, ctx);
    if (s <= 0 && w) continue;
    scored.push({ item, s });
  }
  scored.sort((a, b) => b.s - a.s || a.item.label.length - b.item.label.length);
  return scored.map((x) => x.item);
}

function score(item, w, ctx) {
  let best = 0;
  if (!w) {
    if (ctx?.slot === 'header' && (item.kind === 'hat' || item.filter === 'when' || item.filter === 'define')) best = 80;
    else if (item.kind === 'var' || item.kind === 'list' || item.kind === 'arg') best = 40;
    else if (ctx?.want === 'boolean' && item.kind === 'boolean') best = 50;
    else best = 10;
  } else {
    const filters = item.filter.toLowerCase().split(/\s+/);
    filters.push(item.label.toLowerCase());
    for (const f of filters) {
      if (f === w) best = Math.max(best, 300);
      else if (f.startsWith(w)) best = Math.max(best, 200 - Math.min(f.length, 80));
      else if (w.length >= 3 && f.includes(w)) best = Math.max(best, 90 - f.indexOf(w));
      else if (w.length >= 3 && subseq(f, w)) best = Math.max(best, 30);
    }
  }
  if ((item.kind === 'var' || item.kind === 'list' || item.kind === 'arg') && best >= 200) best += 15;
  if (ctx?.want === 'boolean' && item.kind === 'boolean') best += 20;
  if (ctx?.slot === 'hat' && item.kind === 'hat') best += 10;
  return best;
}

function subseq(text, w) {
  let i = 0;
  for (const ch of text) {
    if (ch === w[i]) i++;
    if (i >= w.length) return true;
  }
  return false;
}

function identToken(text, pos) {
  let i = pos;
  while (i > 0 && /[A-Za-z0-9_]/.test(text[i - 1])) i--;
  let j = pos;
  while (j < text.length && /[A-Za-z0-9_]/.test(text[j])) j++;
  return { start: i, end: j, word: text.slice(i, j) };
}

function quotedToken(line, col, lineStart) {
  let quote = '';
  let start = -1;
  for (let i = 0; i < col; i++) {
    const c = line[i];
    if (!quote && (c === '"' || c === "'")) { quote = c; start = i + 1; }
    else if (quote && c === '\\') i++;
    else if (quote && c === quote) { quote = ''; start = -1; }
  }
  if (!quote || start < 0) return null;
  let end = col;
  for (let i = col; i < line.length; i++) {
    const c = line[i];
    if (c === '\\') { i++; continue; }
    if (c === quote) { end = i; break; }
    end = i + 1;
  }
  return { start: lineStart + start, end: lineStart + end, word: line.slice(start, end) };
}

function commentOnly(line, col) {
  let quote = '';
  for (let i = 0; i < col; i++) {
    const c = line[i];
    if (!quote && c === '#') return true;
    if (!quote && (c === '"' || c === "'")) quote = c;
    else if (quote && c === '\\') i++;
    else if (quote && c === quote) quote = '';
  }
  return false;
}

function parenDepth(s) {
  let d = 0;
  let q = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (!q && (c === '"' || c === "'")) q = c;
    else if (q && c === '\\') i++;
    else if (q && c === q) q = '';
    else if (!q && c === '(') d++;
    else if (!q && c === ')') d = Math.max(0, d - 1);
  }
  return d;
}

function innermostFn(s) {
  const stack = [];
  let q = '';
  let ident = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (!q && (c === '"' || c === "'")) { q = c; ident = ''; }
    else if (q && c === '\\') i++;
    else if (q && c === q) q = '';
    else if (!q && /[A-Za-z_]/.test(c)) ident += c;
    else if (!q && /[0-9]/.test(c) && ident) ident += c;
    else if (!q && c === '(') { stack.push(ident); ident = ''; }
    else if (!q && c === ')') { stack.pop(); ident = ''; }
    else ident = '';
  }
  return stack[stack.length - 1] || '';
}

function firstArgRange(snippet) {
  const i = snippet.indexOf('(');
  const j = snippet.indexOf(')', i + 1);
  if (i < 0 || j < 0 || j <= i + 1) return null;
  const inner = snippet.slice(i + 1, j);
  const comma = inner.search(/\s*,\s*/);
  const end = comma >= 0 ? i + 1 + comma : j;
  return [i + 1, end];
}

function caretCoords(ta) {
  const style = getComputedStyle(ta);
  const lineH = parseFloat(style.lineHeight) || 20;
  const padL = parseFloat(style.paddingLeft) || 0;
  const padT = parseFloat(style.paddingTop) || 0;
  const pos = ta.selectionStart;
  const ahead = ta.value.slice(0, pos);
  const rows = ahead.split('\n');
  const row = rows.length - 1;
  const colText = rows[row];
  const ctx = caretCoords.ctx || (caretCoords.ctx = document.createElement('canvas').getContext('2d'));
  ctx.font = style.font;
  const w = ctx.measureText(colText).width;
  const r = ta.getBoundingClientRect();
  const top = r.top + padT + row * lineH - ta.scrollTop;
  return {
    left: r.left + padL + w - ta.scrollLeft,
    top,
    bottom: top + lineH,
    lineH,
  };
}
